import { useState } from "react";
import { supabase } from "@/lib/supabase";
import {
    uploadSiteImage,
    storagePathFromUrl,
    SITE_IMAGES_BUCKET,
} from "@/lib/supabase";
import { useGallery, useInvalidate } from "@/lib/siteData";
import { Card, Field, SaveBtn, DelBtn, Ok, Err, Notice } from "./adminUI";

export default function GalleryAdmin() {
    const gallery = useGallery(true);
    const invalidate = useInvalidate();
    const [err, setErr] = useState("");
    const [url, setUrl] = useState("");
    const [alt, setAlt] = useState("");

    const refresh = () => invalidate("gallery");

    const remove = async (img) => {
        if (!window.confirm("Excluir esta foto do gerenciador?")) return;
        setErr("");
        const path = storagePathFromUrl(img.url);
        if (path) {
            await supabase.storage.from(SITE_IMAGES_BUCKET).remove([path]);
        }
        const { error } = await supabase
            .from("gallery_images")
            .delete()
            .eq("id", img.id);
        if (error) setErr("Erro ao excluir: " + error.message);
        else refresh();
    };

    const setPrimary = async (img) => {
        setErr("");
        await supabase
            .from("gallery_images")
            .update({ is_primary: false })
            .neq("id", img.id);
        const { error } = await supabase
            .from("gallery_images")
            .update({ is_primary: true })
            .eq("id", img.id);
        if (error) setErr("Erro: " + error.message);
        else refresh();
    };

    const move = async (img, dir) => {
        setErr("");
        const { error } = await supabase
            .from("gallery_images")
            .update({
                sort_order: (Number(img.sort_order) || 0) + dir,
            })
            .eq("id", img.id);
        if (error) setErr("Erro: " + error.message);
        else refresh();
    };

    const saveAlt = async (img, newAlt) => {
        const { error } = await supabase
            .from("gallery_images")
            .update({ alt: newAlt })
            .eq("id", img.id);
        if (error) setErr("Erro: " + error.message);
    };

    const addFromUrl = async () => {
        setErr("");
        if (!url) return;
        const { error } = await supabase
            .from("gallery_images")
            .insert({ url, alt, sort_order: gallery.length });
        if (error) setErr("Erro ao adicionar: " + error.message);
        else {
            setUrl("");
            setAlt("");
            refresh();
        }
    };

    const upload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setErr("");
        try {
            const publicUrl = await uploadSiteImage(file, "gallery");
            const { error } = await supabase
                .from("gallery_images")
                .insert({ url: publicUrl, alt, sort_order: gallery.length });
            if (error) setErr("Erro ao salvar: " + error.message);
            else {
                setAlt("");
                refresh();
            }
        } catch (ex) {
            setErr("Erro no upload: " + ex.message);
        }
    };

    return (
        <div className="space-y-8">
            <h1 className="font-display text-2xl font-black uppercase sm:text-3xl">
                Fotos
            </h1>

            <Notice tone="teal" testid="gallery-info">
                As fotos originais do site (galeria-1, galeria-2 e galeria-3)
                estão preservadas. Excluir aqui remove apenas o registro no
                gerenciador — o arquivo original não é apagado.
            </Notice>

            <Card className="space-y-4">
                <p className="font-mono2 text-[10px] font-semibold uppercase tracking-[0.25em] text-neon-cyan">
                    Adicionar foto
                </p>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field label="Texto alternativo" value={alt} onChange={setAlt} testid="gallery-new-alt" />
                    <Field label="Ou cole uma URL de imagem" value={url} onChange={setUrl} testid="gallery-new-url" />
                </div>
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <label className="inline-flex cursor-pointer items-center rounded-full border border-neon-cyan/40 px-5 py-2.5 font-mono2 text-[10px] font-semibold uppercase tracking-[0.2em] text-neon-cyan">
                        Enviar arquivo
                        <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            data-testid="gallery-upload"
                            onChange={upload}
                        />
                    </label>
                    <SaveBtn onClick={addFromUrl} testid="gallery-add-url">
                        Adicionar por URL
                    </SaveBtn>
                </div>
                <Err msg={err} />
            </Card>

            {gallery.map((img) => (
                <GalleryRow
                    key={img.id}
                    img={img}
                    onRemove={remove}
                    onPrimary={setPrimary}
                    onMove={move}
                    onAlt={saveAlt}
                />
            ))}
        </div>
    );
}

function GalleryRow({ img, onRemove, onPrimary, onMove, onAlt }) {
    const [alt, setAlt] = useState(img.alt);
    return (
        <Card className="flex flex-col gap-5 sm:flex-row">
            <img
                src={img.url}
                alt={img.alt}
                className="h-40 w-full rounded-2xl border border-ink/10 object-cover sm:w-56"
            />
            <div className="flex min-w-0 flex-1 flex-col gap-3">
                <div className="flex flex-wrap items-center gap-2">
                    {img.is_primary && (
                        <span className="rounded-full border border-neon-cyan/40 bg-neon-cyan/10 px-3 py-1 font-mono2 text-[9px] font-semibold uppercase tracking-[0.2em] text-neon-cyan" data-testid={`gallery-primary-badge-${img.id}`}>
                            Principal
                        </span>
                    )}
                    <div className="ml-auto flex items-center gap-2">
                        <button
                            onClick={() => onMove(img, -1)}
                            data-testid={`gallery-up-${img.id}`}
                            className="rounded-full border border-ink/20 px-3 py-1.5 font-mono2 text-[10px] text-dim hover:text-ink"
                        >
                            ↑
                        </button>
                        <button
                            onClick={() => onMove(img, +1)}
                            data-testid={`gallery-down-${img.id}`}
                            className="rounded-full border border-ink/20 px-3 py-1.5 font-mono2 text-[10px] text-dim hover:text-ink"
                        >
                            ↓
                        </button>
                    </div>
                </div>
                <Field
                    label="Texto alternativo"
                    value={alt}
                    onChange={setAlt}
                    testid={`gallery-alt-${img.id}`}
                />
                <div className="flex flex-wrap items-center gap-3">
                    {!img.is_primary && (
                        <button
                            onClick={() => onPrimary(img)}
                            data-testid={`gallery-set-primary-${img.id}`}
                            className="rounded-full border border-neon-cyan/40 px-5 py-2.5 font-display text-[10px] font-extrabold uppercase tracking-widest text-neon-cyan transition-colors hover:bg-neon-cyan/10"
                        >
                            Tornar principal
                        </button>
                    )}
                    <SaveBtn onClick={() => onAlt(img, alt)} testid={`gallery-save-${img.id}`}>
                        Salvar texto
                    </SaveBtn>
                    <DelBtn onClick={() => onRemove(img)} testid={`gallery-delete-${img.id}`} />
                </div>
            </div>
        </Card>
    );
}
